import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { inviteStatusSchema } from '../enums/invite-status.enum.js'

/**
 * The shape the API returns for an invite on the authenticated management
 * surface. Note there is no token field: the raw token exists only in the
 * emailed link and is never returned by the API, so a signed-in user cannot
 * read another person's invite link out of a list response.
 *
 * Dates cross the wire as ISO strings.
 */
export const inviteDtoSchema = z.object({
  id: uuidSchema,
  email: z.string().email(),
  status: inviteStatusSchema,
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** Display name of the inviter, or null if that user has since been deleted. */
  invitedByName: z.string().nullable(),
})

export type InviteDto = z.infer<typeof inviteDtoSchema>

export const inviteListDtoSchema = z.object({
  invites: z.array(inviteDtoSchema),
  total: z.number().int().nonnegative(),
})

export type InviteListDto = z.infer<typeof inviteListDtoSchema>

/**
 * What the PUBLIC accept-invite page is told about a token it holds. Kept to
 * the bare minimum needed to render the form — the email the invite was sent
 * to, so the recipient can confirm they are claiming the right account. No ids,
 * no inviter, no timestamps: this endpoint is unauthenticated.
 */
export const invitePreviewDtoSchema = z.object({
  email: z.string().email(),
})

export type InvitePreviewDto = z.infer<typeof invitePreviewDtoSchema>
